import { useCallback, useEffect, useRef } from 'react';
import { bearingDegrees, haversineMeters, lerpAngle, lerpLatLng, type LatLng } from '@tcrd/shared';
import type { MapEngine } from './engine';

interface Fix extends LatLng {
  heading: number | null;
  at: number;
}

/**
 * Anima el carrito ENTRE posiciones GPS reales. Nunca extrapola: si no
 * llegan nuevos puntos el vehículo se queda en la última posición conocida
 * (y se atenúa cuando el dato es antiguo).
 */
export function useCarAnimation(engine: MapEngine | null, opts: { follow?: boolean } = {}) {
  const from = useRef<{ pos: LatLng; heading: number } | null>(null);
  const to = useRef<{ pos: LatLng; heading: number } | null>(null);
  const start = useRef(0);
  const duration = useRef(1000);
  const lastFix = useRef<Fix | null>(null);
  const stale = useRef(false);
  const shown = useRef<{ pos: LatLng; heading: number } | null>(null);
  const raf = useRef<number | null>(null);
  const follow = useRef(opts.follow ?? false);
  follow.current = opts.follow ?? false;

  const frame = useCallback(() => {
    raf.current = null;
    if (!engine || !to.current) return;
    const t = Math.min(1, (performance.now() - start.current) / duration.current);
    const a = from.current ?? to.current;
    const pos = lerpLatLng(a.pos, to.current.pos, t);
    const heading = lerpAngle(a.heading, to.current.heading, Math.min(1, t * 2));
    shown.current = { pos, heading };
    engine.setCar({ position: pos, heading, stale: stale.current });
    if (t < 1) raf.current = requestAnimationFrame(frame);
    else if (follow.current) engine.panTo(pos);
  }, [engine]);

  const push = useCallback(
    (fix: Fix, isStale = false) => {
      stale.current = isStale;
      const prev = lastFix.current;
      if (prev && fix.at <= prev.at) return; // puntos antiguos o repetidos no mueven el carrito
      let heading = fix.heading ?? to.current?.heading ?? 0;
      if ((fix.heading === null || fix.heading === undefined) && prev && haversineMeters(prev, fix) > 3) heading = bearingDegrees(prev, fix);
      from.current = shown.current ?? (prev ? { pos: prev, heading } : null);
      to.current = { pos: { lat: fix.lat, lng: fix.lng }, heading };
      // La animación dura lo que tardó en llegar el punto (entre 0.6 y 6 s).
      duration.current = prev ? Math.max(600, Math.min(6000, fix.at - prev.at)) : 1;
      start.current = performance.now();
      lastFix.current = fix;
      if (!prev && engine) {
        // Primera posición: se dibuja de inmediato, sin animación.
        shown.current = to.current;
        engine.setCar({ position: to.current.pos, heading, stale: stale.current });
        return;
      }
      if (raf.current === null) raf.current = requestAnimationFrame(frame);
    },
    [frame, engine],
  );

  const setStale = useCallback(
    (s: boolean) => {
      if (stale.current === s) return;
      stale.current = s;
      if (engine && shown.current) engine.setCar({ position: shown.current.pos, heading: shown.current.heading, stale: s });
    },
    [engine],
  );

  const clear = useCallback(() => {
    lastFix.current = null;
    from.current = to.current = shown.current = null;
    engine?.setCar(null);
  }, [engine]);

  useEffect(() => () => void (raf.current !== null && cancelAnimationFrame(raf.current)), []);

  return { push, setStale, clear, current: () => shown.current?.pos ?? null };
}
